import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Shield, Lock, Plus, Trash2, CheckCircle2, Mail, CalendarClock, Send, Clock, RefreshCw, X } from "lucide-react";
import { API, useAuth } from "@/App";
import { useI18n } from "@/i18n";
import { ShadowToggle } from "@/components/gex/ReasonCodeRenderer";

function SessionActivityTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/sessions?limit=50`);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch {}
    setLoading(false);
  };

  const parseUA = (ua) => {
    if (!ua || ua === "unknown") return "Unknown";
    if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
    if (ua.includes("Edg")) return "Edge";
    return ua.length > 30 ? ua.slice(0, 30) + "..." : ua;
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#00FF88]" size={24} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-[#00FF88]" />
          <div>
            <p className="text-sm font-bold text-white">Login Activity</p>
            <p className="text-[10px] text-[#8A8A93] font-mono">{total} total event{total !== 1 ? "s" : ""} recorded</p>
          </div>
        </div>
        <button onClick={fetchLogs} className="text-xs flex items-center gap-1.5 text-[#8A8A93] hover:text-white transition-colors">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="bg-[#121216] border border-[#26262B] rounded-sm p-8 text-center">
          <Shield size={32} className="text-[#8A8A93] mx-auto mb-3" />
          <p className="text-sm text-[#8A8A93]">No login activity recorded yet</p>
        </div>
      ) : (
        <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-hidden">
          <div className="grid grid-cols-[120px_1fr_140px_100px] gap-3 px-4 py-2.5 border-b border-[#26262B] bg-[#0A0A0D]">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Status</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Email / IP</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Timestamp</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Browser</span>
          </div>
          <div className="divide-y divide-[#1A1A20] max-h-[480px] overflow-y-auto">
            {logs.map((log, i) => {
              const isSuccess = log.event === "login_success";
              const ts = log.timestamp ? new Date(log.timestamp) : null;
              return (
                <div key={i} data-testid={`session-log-row-${i}`} className="grid grid-cols-[120px_1fr_140px_100px] gap-3 px-4 py-3 hover:bg-[#1A1A20]/50 transition-colors items-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSuccess ? "bg-[#00FF88]" : "bg-[#FF3366]"}`} />
                    <span className={`text-[11px] font-mono font-bold ${isSuccess ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
                      {isSuccess ? "SUCCESS" : "FAILED"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-mono text-white truncate">{log.email || "—"}</p>
                    <p className="text-[10px] font-mono text-[#8A8A93] truncate">{log.ip || "—"}</p>
                  </div>
                  <div className="min-w-0">
                    {ts ? <>
                      <p className="text-[11px] font-mono text-white">{ts.toLocaleDateString()}</p>
                      <p className="text-[10px] font-mono text-[#8A8A93]">{ts.toLocaleTimeString()}</p>
                    </> : <span className="text-[11px] text-[#8A8A93]">—</span>}
                  </div>
                  <span className="text-[10px] font-mono text-[#8A8A93] truncate" title={log.user_agent}>
                    {parseUA(log.user_agent)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function UserManagementTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("viewer");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editPass, setEditPass] = useState("");

  useEffect(() => { fetchUsers(); }, []);
  const fetchUsers = async () => { setLoading(true); try { const r = await axios.get(`${API}/admin/users`); setUsers(r.data); } catch {} setLoading(false); };

  const createUser = async () => {
    if (!newEmail || !newPass) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, { email: newEmail, password: newPass, name: newName, role: newRole });
      setNewEmail(""); setNewPass(""); setNewName(""); setNewRole("viewer"); setShowCreate(false);
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    setCreating(false);
  };

  const updatePassword = async (id) => {
    if (!editPass || editPass.length < 6) { alert("Min 6 chars"); return; }
    try { await axios.put(`${API}/admin/users/${id}`, { password: editPass }); setEditId(null); setEditPass(""); alert("Password updated"); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const toggleRole = async (id, currentRole) => {
    const newR = currentRole === "admin" ? "viewer" : "admin";
    try { await axios.put(`${API}/admin/users/${id}`, { role: newR }); fetchUsers(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await axios.delete(`${API}/admin/users/${id}`); fetchUsers(); } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#00FF88]" size={24} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-white">{users.length} user{users.length !== 1 ? "s" : ""}</p>
        <button onClick={() => setShowCreate(!showCreate)} data-testid="create-user-btn" className="text-xs flex items-center gap-1.5 px-3 py-1.5 bg-[#00FF88] text-[#08080A] font-bold rounded-sm hover:bg-[#33FF9F]">
          <Plus size={12} /> New User
        </button>
      </div>

      {showCreate && (
        <div className="bg-[#121216] border border-[#00FF88]/20 rounded-sm p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">Email</label><input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2 text-sm font-mono" placeholder="user@email.com" /></div>
            <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">Password</label><input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2 text-sm font-mono" placeholder="min 6 chars" /></div>
            <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">Name</label><input type="text" value={newName} onChange={e => setNewName(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2 text-sm font-mono" placeholder="optional" /></div>
            <div><label className="text-[10px] font-bold uppercase text-[#8A8A93] mb-1 block">Role</label>
              <div className="flex gap-2">{["viewer", "admin"].map(r => <button key={r} onClick={() => setNewRole(r)} className={`flex-1 px-3 py-2 text-sm rounded-sm border font-mono ${newRole === r ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93]"}`}>{r}</button>)}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createUser} disabled={creating || !newEmail || !newPass} className="bg-[#00FF88] text-[#08080A] font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-40">{creating ? "Creating..." : "Create"}</button>
            <button onClick={() => setShowCreate(false)} className="text-[#8A8A93] text-sm px-3">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-hidden">
        <div className="grid grid-cols-[1fr_120px_80px_120px] gap-3 px-4 py-2.5 border-b border-[#26262B] bg-[#0A0A0D]">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">User</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Role</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Password</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#8A8A93]">Actions</span>
        </div>
        <div className="divide-y divide-[#1A1A20]">
          {users.map(u => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-[1fr_120px_80px_120px] gap-3 px-4 py-3 items-center">
              <div>
                <p className="text-xs font-mono text-white">{u.email}</p>
                <p className="text-[10px] text-[#8A8A93]">{u.name || "—"}</p>
              </div>
              <button onClick={() => toggleRole(u.id, u.role)} className={`text-[10px] px-2 py-1 rounded-sm font-mono font-bold border w-fit ${u.role === "admin" ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10" : "border-[#3F3F47] text-[#8A8A93]"}`}>{u.role}</button>
              <div>
                {editId === u.id ? (
                  <div className="flex gap-1"><input type="password" value={editPass} onChange={e => setEditPass(e.target.value)} className="w-20 bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1 text-[10px] font-mono" placeholder="new pass" /><button onClick={() => updatePassword(u.id)} className="text-[#00FF88]"><CheckCircle2 size={12} /></button></div>
                ) : (
                  <button onClick={() => { setEditId(u.id); setEditPass(""); }} className="text-[10px] text-[#8A8A93] hover:text-white">Reset</button>
                )}
              </div>
              <button onClick={() => deleteUser(u.id)} className="text-[10px] text-[#8A8A93] hover:text-[#FF3366]"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState(null); const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false); const [testing, setTesting] = useState(false); const [testResult, setTestResult] = useState(null); const [tradierKey, setTradierKey] = useState(""); const [ttCID, setTtCID] = useState(""); const [ttCS, setTtCS] = useState(""); const [activeTab, setActiveTab] = useState("broker"); const [oauthBanner, setOauthBanner] = useState(null);
  useEffect(() => { fetchSettings(); }, []);
  // React to OAuth callback redirect: ?tt_connected=1 or ?tt_error=...
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("tt_connected")) {
      setOauthBanner({ kind: "ok", msg: "Tastytrade connected successfully via OAuth Application." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (qs.get("tt_error")) {
      setOauthBanner({ kind: "err", msg: `Tastytrade connection failed: ${qs.get("tt_error")}` });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);
  const fetchSettings = async () => { setLoading(true); try { const r = await axios.get(`${API}/settings`); setSettings(r.data); } catch {} setLoading(false); };
  const saveSetting = async (k, v) => { setSaving(true); try { await axios.put(`${API}/settings`, { [k]: v }); fetchSettings(); } catch (e) { alert(e.response?.data?.detail || "Error"); } setSaving(false); };
  const saveMultiple = async (u) => { setSaving(true); try { await axios.put(`${API}/settings`, u); await fetchSettings(); } catch (e) { alert(e.response?.data?.detail || "Error"); } setSaving(false); };
  const testConnection = async (b) => { setTesting(true); setTestResult(null); try { const r = await axios.post(`${API}/broker/test-connection?broker=${b}`); setTestResult(r.data); } catch (e) { setTestResult({ connected: false, error: e.response?.data?.detail || "Failed" }); } setTesting(false); };
  const connectTastytrade = async () => {
    // Persist whatever the admin just typed before redirecting
    const pending = {};
    if (ttCID) pending.tastytrade_client_id = ttCID;
    if (ttCS) pending.tastytrade_client_secret = ttCS;
    if (Object.keys(pending).length) {
      try { await axios.put(`${API}/settings`, pending); } catch (e) { alert(e.response?.data?.detail || "Could not save credentials"); return; }
    }
    try {
      // Iter119.33 — explicitly pass our own origin so the backend doesn't
      // have to guess from x-forwarded-host. Production behind a custom
      // CNAME (e.g. forge.arxmentis.ltd) was getting the ingress's
      // internal hostname, which broke OAuth.
      const redirectUri = `${window.location.origin}/api/oauth/tastytrade/callback`;
      const r = await axios.get(`${API}/oauth/tastytrade/authorize`, {
        params: { redirect_uri: redirectUri },
      });
      // Hand off the browser to Tastytrade's OAuth login (handles 2FA there)
      window.location.href = r.data.url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start Tastytrade authorization");
    }
  };
  const disconnectTastytrade = async () => {
    if (!window.confirm("Disconnect Tastytrade? You'll need to re-authorize to use it again.")) return;
    try { await axios.post(`${API}/oauth/tastytrade/disconnect`); fetchSettings(); setTestResult(null); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  if (loading || !settings) return <div className="flex items-center justify-center h-full min-h-[60vh]"><Loader2 className="animate-spin text-[#00FF88]" size={32} /></div>;
  const allTabs = [{ id: "broker", label: t("settings.tab_brokers") }, { id: "strategy", label: t("settings.tab_strategy") }, { id: "schedules", label: t("settings.tab_scheduled"), adminOnly: true }, { id: "users", label: t("settings.tab_users"), adminOnly: true }, { id: "experimental", label: "Experimental" }, { id: "activity", label: t("settings.tab_session") }];
  const tabs = allTabs.filter(tab => !tab.adminOnly || isAdmin);
  return (
    <div className="animate-fade-in max-w-3xl"><h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo'] mb-6">{t("settings.title")}</h2>
      {!isAdmin && (
        <div data-testid="viewer-readonly-banner" className="mb-5 px-4 py-3 border border-[#FFB800]/20 bg-[#FFB800]/5 rounded-sm flex items-center gap-2">
          <Lock size={14} className="text-[#FFB800]" />
          <span className="text-xs text-[#FFB800] font-mono">{t("settings.viewer_locked")}</span>
        </div>
      )}
      <div className="flex gap-1 mb-6 border-b border-[#26262B]">{tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`settings-tab-${tab.id}`} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${activeTab === tab.id ? "border-[#00FF88] text-[#00FF88]" : "border-transparent text-[#8A8A93] hover:text-white"}`}>{tab.label}</button>)}</div>
      {activeTab === "broker" && <div className="space-y-6">
        <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5"><h3 className="text-sm font-bold uppercase text-[#8A8A93] mb-4">Active Broker</h3><div className="flex gap-2"><button onClick={() => isAdmin && saveSetting("active_broker", "tradier")} disabled={!isAdmin} className={`px-4 py-2 text-sm rounded-sm border ${settings.active_broker === "tradier" ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93]"} ${!isAdmin && "opacity-60 cursor-not-allowed"}`}>Tradier</button><button onClick={() => isAdmin && saveSetting("active_broker", "tastytrade")} disabled={!isAdmin} className={`px-4 py-2 text-sm rounded-sm border ${settings.active_broker === "tastytrade" ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93]"} ${!isAdmin && "opacity-60 cursor-not-allowed"}`}>Tastytrade</button></div></div>
        <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5"><h3 className="text-sm font-bold uppercase text-[#8A8A93] mb-4">Tradier</h3><div className="flex gap-2 mb-3"><input type="password" value={tradierKey} onChange={e => setTradierKey(e.target.value)} placeholder={settings.tradier_api_key_masked || "API token"} disabled={!isAdmin} className="flex-1 bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono disabled:opacity-50" /><button onClick={() => tradierKey && saveSetting("tradier_api_key", tradierKey)} disabled={!isAdmin || !tradierKey} className="bg-[#00FF88] text-[#08080A] font-bold px-4 py-2.5 rounded-sm text-sm disabled:opacity-40">Save</button></div><button onClick={() => testConnection("tradier")} disabled={!isAdmin || testing} className="text-sm border border-[#3F3F47] text-white px-4 py-1.5 rounded-sm disabled:opacity-40">{testing ? "..." : "Test Connection"}</button></div>
        <div data-testid="tastytrade-card" className="bg-[#121216] border border-[#26262B] rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold uppercase text-[#8A8A93]">Tastytrade OAuth2 Application</h3>
            {settings.tastytrade_connected ? (
              <span data-testid="tastytrade-status" className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20">Connected</span>
            ) : (
              <span data-testid="tastytrade-status" className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/20">Not Connected</span>
            )}
          </div>
          {oauthBanner && (
            <div data-testid="tastytrade-oauth-banner" className={`mb-3 p-3 rounded-sm border text-xs font-mono ${oauthBanner.kind === "ok" ? "border-[#00FF88]/20 bg-[#00FF88]/5 text-[#00FF88]" : "border-[#FF3366]/20 bg-[#FF3366]/5 text-[#FF3366]"}`}>{oauthBanner.msg}</div>
          )}
          <p className="text-[11px] text-[#8A8A93] mb-3 leading-relaxed">
            Register an OAuth Application at <span className="text-white">my.tastytrade.com → Manage → API → OAuth Applications</span>.
            Set the redirect URI to:<br/>
            <code className="text-[#00FF88] text-[10px]">{`${process.env.REACT_APP_BACKEND_URL}/api/oauth/tastytrade/callback`}</code>
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Client ID</label>
              <input data-testid="tastytrade-client-id" type="text" value={ttCID} onChange={e => setTtCID(e.target.value)} placeholder={settings.tastytrade_client_id || "fd206b2e-...-c621b"} disabled={!isAdmin} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono disabled:opacity-50" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Client Secret</label>
              <input data-testid="tastytrade-client-secret" type="password" value={ttCS} onChange={e => setTtCS(e.target.value)} placeholder={settings.tastytrade_client_secret_masked || "Client Secret (one-time visible value)"} disabled={!isAdmin} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono disabled:opacity-50" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button data-testid="tastytrade-save" onClick={() => { const u = {}; if (ttCID) u.tastytrade_client_id = ttCID; if (ttCS) u.tastytrade_client_secret = ttCS; if (Object.keys(u).length) saveMultiple(u); }} disabled={!isAdmin || saving || (!ttCID && !ttCS)} className="bg-[#26262B] text-white font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-40">Save Credentials</button>
              <button data-testid="tastytrade-connect" onClick={connectTastytrade} disabled={!isAdmin} className="bg-[#00FF88] text-[#08080A] font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-40">{settings.tastytrade_connected ? "Re-authorize" : "Connect Tastytrade"}</button>
              <button data-testid="tastytrade-test" onClick={() => testConnection("tastytrade")} disabled={!isAdmin || testing || !settings.tastytrade_connected} className="text-sm border border-[#3F3F47] text-white px-4 py-2 rounded-sm disabled:opacity-40">{testing ? "..." : "Test"}</button>
              {settings.tastytrade_connected && (
                <button data-testid="tastytrade-disconnect" onClick={disconnectTastytrade} disabled={!isAdmin} className="text-sm border border-[#FF3366]/30 text-[#FF3366] px-4 py-2 rounded-sm disabled:opacity-40">Disconnect</button>
              )}
            </div>
          </div>
        </div>
        {testResult && <div className={`p-3 rounded-sm border text-sm font-mono ${testResult.connected ? "border-[#00FF88]/20 bg-[#00FF88]/5 text-[#00FF88]" : "border-[#FF3366]/20 bg-[#FF3366]/5 text-[#FF3366]"}`}>{testResult.connected ? (testResult.message || "Connected!") : `Failed: ${testResult.error}`}</div>}
      </div>}
      {activeTab === "strategy" && <div className="space-y-6">
        <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5"><h3 className="text-sm font-bold uppercase text-[#8A8A93] mb-4">Pricing & Liquidity</h3><div className="grid grid-cols-2 gap-4"><KnobInput label="Sell Bias" field="sell_bias" value={settings.sell_bias} type="number" step={0.05} onSave={saveSetting} /><KnobInput label="Min OI" field="oi_min" value={settings.oi_min} type="number" step={50} onSave={saveSetting} /><KnobInput label="Max BA" field="ba_max" value={settings.ba_max} type="number" step={0.01} onSave={saveSetting} /><KnobInput label="ITM Delta" field="itm_call_delta" value={settings.itm_call_delta} type="number" step={0.05} onSave={saveSetting} /><KnobInput label="Min ROI % (Forge gate)" field="min_income_pct" value={settings.min_income_pct ?? 3.0} type="number" step={0.5} onSave={saveSetting} /></div></div>
      </div>}
      {activeTab === "activity" && <SessionActivityTab />}
      {activeTab === "users" && isAdmin && <UserManagementTab />}
      {activeTab === "schedules" && isAdmin && <ScheduledScansTab />}
      {activeTab === "experimental" && <ExperimentalTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter122.7 §1 — Experimental / Shadow Modes tab
// ---------------------------------------------------------------------------
function ExperimentalTab() {
  return (
    <div className="space-y-4" data-testid="experimental-tab">
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5">
        <h3 className="text-sm font-bold uppercase text-[#8A8A93] mb-1">
          Experimental / Shadow Modes
        </h3>
        <p className="text-[11px] text-[#8A8A93] leading-relaxed mb-4">
          Opt-in previews of engine variants that are still being validated.
          v1 remains the production default and continues to drive every
          trading decision — shadow outputs are display-only.
        </p>
        <ShadowToggle />
        <div className="mt-4 pt-3 border-t border-[#26262B] text-[11px] text-[#8A8A93] leading-relaxed">
          Need a deeper look? Eligible testers can open the dedicated{" "}
          <a href="/shadow" className="text-[#00FF88] underline"
              data-testid="shadow-mode-workspace-link">
            /shadow diagnostics workspace
          </a>{" "}
          to compare v1 and v2 side by side.
        </div>
      </div>
    </div>
  );
}

function KnobInput({ label, field, value, type = "text", step, onSave }) {
  const [val, setVal] = useState(value ?? ""); const [dirty, setDirty] = useState(false);
  useEffect(() => { setVal(value ?? ""); setDirty(false); }, [value]);
  const save = () => { if (!dirty) return; const p = type === "number" ? parseFloat(val) : val; if (type === "number" && isNaN(p)) return; onSave(field, p); setDirty(false); };
  return (<div><label className="text-xs font-bold uppercase text-[#8A8A93] mb-2 block">{label}</label><div className="flex gap-1"><input type={type} step={step} value={val} onChange={e => { setVal(e.target.value); setDirty(true); }} onBlur={save} onKeyDown={e => e.key === "Enter" && save()} className="flex-1 bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono" />{dirty && <button onClick={save} className="text-[#00FF88] px-2"><CheckCircle2 size={14} /></button>}</div></div>);
}

// ==================== SCHEDULED SCANS (admin) ====================
function ScheduledScansTab() {
  const [data, setData] = useState({ auto_scans: [], custom_scans: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/scheduled-scans`); setData(r.data); } catch {}
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  const onToggle = async (id) => {
    setBusyId(id);
    try { await axios.put(`${API}/scheduled-scans/${id}/toggle`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "Error"); }
    setBusyId(null);
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this scheduled scan? This cannot be undone.")) return;
    setBusyId(id);
    try { await axios.delete(`${API}/scheduled-scans/${id}`); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "Error"); }
    setBusyId(null);
  };
  const onRunNow = async (id) => {
    setBusyId(id);
    try { await axios.post(`${API}/scheduled-scans/${id}/run-now`); alert("Scan started — recipients will receive the email when it completes."); await fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "Error"); }
    setBusyId(null);
  };
  const onSaveSchedule = async (payload, id) => {
    try {
      if (id) await axios.put(`${API}/scheduled-scans/${id}`, payload);
      else await axios.post(`${API}/scheduled-scans`, payload);
      setShowForm(false); setEditing(null); await fetchAll();
    } catch (e) { alert(e.response?.data?.detail || "Could not save schedule"); }
  };
  const sendTest = async () => {
    if (!emailTo.trim()) return;
    setEmailStatus({ kind: "loading" });
    try { const r = await axios.post(`${API}/admin/email/test`, { to: emailTo.trim() }); setEmailStatus({ kind: "ok", msg: `Sent to ${(r.data.recipients || []).join(", ")}` }); }
    catch (e) { setEmailStatus({ kind: "err", msg: e.response?.data?.detail || "Failed" }); }
  };
  const sendMonthlyDigest = async () => {
    setEmailStatus({ kind: "loading" });
    try {
      const r = await axios.post(`${API}/admin/email/monthly-digest-now`);
      const recips = (r.data.recipients || []).join(", ");
      setEmailStatus({ kind: r.data.ok ? "ok" : "err", msg: r.data.ok ? `Monthly digest sent to ${recips}` : (r.data.error || "Send failed") });
    } catch (e) {
      setEmailStatus({ kind: "err", msg: e.response?.data?.detail || "Failed" });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#00FF88]" size={28} /></div>;

  return (
    <div className="space-y-6" data-testid="scheduled-scans-tab">
      {/* SMTP test */}
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={14} className="text-[#00FF88]" />
          <h3 className="text-sm font-bold uppercase text-[#8A8A93]">Email delivery test</h3>
        </div>
        <p className="text-[11px] text-[#8A8A93] mb-3">Send a test email to confirm SMTP works for your recipients before scheduling scans.</p>
        <div className="flex gap-2">
          <input data-testid="smtp-test-to" type="email" placeholder="recipient@example.com" value={emailTo} onChange={e => setEmailTo(e.target.value)} className="flex-1 bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-sm font-mono" />
          <button data-testid="smtp-test-send" onClick={sendTest} disabled={!emailTo.trim() || emailStatus?.kind === "loading"} className="bg-[#00FF88] text-[#08080A] font-bold px-4 py-2.5 rounded-sm text-sm disabled:opacity-40 flex items-center gap-2">
            {emailStatus?.kind === "loading" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Test
          </button>
        </div>
        <div className="mt-3 pt-3 border-t border-[#26262B] flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-[#8A8A93] flex-1 min-w-[200px]">
            <strong className="text-white">Monthly Digest</strong> · Decision Score Card recap (wins / losses / win rate / top trades) auto-sent on the 1st of every month at 9:00 AM ET to all admins.
          </p>
          <button
            data-testid="send-monthly-digest-now"
            onClick={sendMonthlyDigest}
            disabled={emailStatus?.kind === "loading"}
            className="text-xs border border-[#FFB800]/40 text-[#FFB800] hover:bg-[#FFB800]/10 px-3 py-2 rounded-sm font-bold disabled:opacity-40 flex items-center gap-2"
          >
            {emailStatus?.kind === "loading" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send Digest Now
          </button>
        </div>
        {emailStatus && emailStatus.kind !== "loading" && (
          <div className={`mt-3 text-xs font-mono ${emailStatus.kind === "ok" ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
            {emailStatus.kind === "ok" ? `✓ ${emailStatus.msg}` : `✗ ${emailStatus.msg}`}
          </div>
        )}
      </div>

      {/* Custom scheduled scans */}
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarClock size={14} className="text-[#00FF88]" />
            <h3 className="text-sm font-bold uppercase text-[#8A8A93]">Custom Scheduled Scans</h3>
            <span className="text-[10px] text-[#8A8A93] font-mono">({data.custom_scans?.length || 0})</span>
          </div>
          <button data-testid="add-schedule-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1.5 bg-[#00FF88] text-[#08080A] font-bold px-3 py-1.5 rounded-sm text-xs">
            <Plus size={12} /> New Schedule
          </button>
        </div>
        {showForm && <ScheduleFormModal initial={editing} onSubmit={onSaveSchedule} onClose={() => { setShowForm(false); setEditing(null); }} />}
        {(!data.custom_scans || data.custom_scans.length === 0) ? (
          <p className="text-xs text-[#8A8A93] py-3">No custom schedules yet. Create one to receive email alerts when scans complete.</p>
        ) : (
          <div className="space-y-2">
            {data.custom_scans.map(s => (
              <div key={s.id} data-testid={`schedule-row-${s.id}`} className="border border-[#26262B] rounded-sm p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-white truncate">{s.name}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm ${s.enabled ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "bg-[#3F3F47]/30 text-[#8A8A93] border border-[#3F3F47]"}`}>{s.enabled ? "ON" : "OFF"}</span>
                  </div>
                  <div className="text-[11px] text-[#8A8A93] font-mono space-y-0.5">
                    <div>{s.scan_target === "watchlist" ? "Watchlist" : s.scan_target === "market" ? "Full Market (S&P 500 + Russell 1000)" : `Symbols: ${(s.symbols || []).slice(0, 5).join(", ")}${(s.symbols || []).length > 5 ? "…" : ""}`}</div>
                    <div>{(s.schedule_type || "weekly") === "daily" ? `Daily Mon–Fri @ ${s.time_et} ET` : `Weekly on ${(s.day_of_week || "fri").toUpperCase()} @ ${s.time_et} ET`}</div>
                    {s.next_run && <div className="text-[#8A8A93]/70">Next: {s.next_run}</div>}
                    <div>Recipients: <span className="text-[#E4E4E7]">{(s.recipients || []).join(", ") || "—"}</span></div>
                    {s.last_run && <div>Last run: {new Date(s.last_run).toLocaleString()} {s.last_status && <span className={s.last_status.startsWith("error") ? "text-[#FF3366]" : "text-[#00FF88]"}>· {s.last_status}</span>}</div>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button data-testid={`schedule-runnow-${s.id}`} onClick={() => onRunNow(s.id)} disabled={busyId === s.id} className="text-[10px] border border-[#00FF88]/30 text-[#00FF88] px-2.5 py-1 rounded-sm hover:bg-[#00FF88]/5 disabled:opacity-40">Run Now</button>
                  <button data-testid={`schedule-edit-${s.id}`} onClick={() => { setEditing(s); setShowForm(true); }} className="text-[10px] border border-[#3F3F47] text-white px-2.5 py-1 rounded-sm">Edit</button>
                  <button data-testid={`schedule-toggle-${s.id}`} onClick={() => onToggle(s.id)} disabled={busyId === s.id} className="text-[10px] border border-[#3F3F47] text-[#8A8A93] px-2.5 py-1 rounded-sm disabled:opacity-40">{s.enabled ? "Disable" : "Enable"}</button>
                  <button data-testid={`schedule-delete-${s.id}`} onClick={() => onDelete(s.id)} disabled={busyId === s.id} className="text-[10px] border border-[#FF3366]/30 text-[#FF3366] px-2.5 py-1 rounded-sm disabled:opacity-40">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Built-in jobs (read-only) */}
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-5">
        <h3 className="text-sm font-bold uppercase text-[#8A8A93] mb-3 flex items-center gap-2"><Clock size={14} className="text-[#8A8A93]" /> Built-in Jobs</h3>
        <p className="text-[11px] text-[#8A8A93] mb-3">These run automatically and are not editable. Add a Custom Scheduled Scan above to get emails.</p>
        <div className="space-y-1.5">
          {(data.auto_scans || []).filter(j => !j.id.startsWith("custom_scan_")).map(j => (
            <div key={j.id} className="text-[11px] font-mono text-[#8A8A93] flex justify-between gap-3">
              <span className="text-white truncate">{j.name}</span>
              <span className="shrink-0">{j.next_run || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScheduleFormModal({ initial, onSubmit, onClose }) {
  const [name, setName] = useState(initial?.name || "");
  const [scanTarget, setScanTarget] = useState(initial?.scan_target || "watchlist");
  const [symbols, setSymbols] = useState((initial?.symbols || []).join(", "));
  const [scheduleType, setScheduleType] = useState(initial?.schedule_type || "weekly");
  const [dayOfWeek, setDayOfWeek] = useState(initial?.day_of_week || "fri");
  const [timeEt, setTimeEt] = useState(initial?.time_et || "15:30");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [recipients, setRecipients] = useState((initial?.recipients || []).join(", "));
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { alert("Name required"); return; }
    if (scanTarget === "symbols" && !symbols.trim()) { alert("Add at least one symbol or pick Watchlist / Market"); return; }
    setSubmitting(true);
    const payload = {
      name: name.trim(),
      scan_target: scanTarget,
      symbols: scanTarget === "symbols" ? symbols.split(",").map(s => s.trim().toUpperCase()).filter(Boolean) : [],
      schedule_type: scheduleType,
      day_of_week: dayOfWeek,
      time_et: timeEt,
      enabled,
      recipients: recipients.split(",").map(r => r.trim()).filter(Boolean),
    };
    await onSubmit(payload, initial?.id);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} data-testid="schedule-form" className="bg-[#121216] border border-[#26262B] rounded-sm p-6 w-[520px] max-w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black tracking-tight text-white">{initial ? "Edit Schedule" : "New Scheduled Scan"}</h3>
          <button type="button" onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Name</label>
            <input data-testid="schedule-name" required value={name} onChange={e => setName(e.target.value)} placeholder='e.g. "Friday afternoon Watchlist"' className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Scan Target</label>
            <div className="flex gap-2">
              {["watchlist", "market", "symbols"].map(t => (
                <button key={t} type="button" onClick={() => setScanTarget(t)} data-testid={`schedule-target-${t}`} className={`px-3 py-2 text-xs rounded-sm border ${scanTarget === t ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93]"}`}>{t === "watchlist" ? "Watchlist" : t === "market" ? "Full Market" : "Custom Symbols"}</button>
              ))}
            </div>
          </div>
          {scanTarget === "symbols" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Symbols (comma-separated)</label>
              <input data-testid="schedule-symbols" value={symbols} onChange={e => setSymbols(e.target.value)} placeholder="AAPL, MSFT, NVDA" className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono" />
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Frequency</label>
              <select data-testid="schedule-frequency" value={scheduleType} onChange={e => setScheduleType(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm">
                <option value="weekly">Weekly</option>
                <option value="daily">Daily (Mon–Fri)</option>
              </select>
            </div>
            {scheduleType === "weekly" && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Day</label>
                <select data-testid="schedule-day" value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm">
                  {[["mon","Mon"],["tue","Tue"],["wed","Wed"],["thu","Thu"],["fri","Fri"],["sat","Sat"],["sun","Sun"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Time (ET)</label>
              <input data-testid="schedule-time" type="time" value={timeEt} onChange={e => setTimeEt(e.target.value)} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-3 py-2.5 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">Email Recipients (comma-separated)</label>
            <textarea data-testid="schedule-recipients" rows={2} value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="admin@arxmentis.ltd, viewer@arxmentis.ltd" className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2.5 text-sm font-mono" />
            <p className="text-[10px] text-[#8A8A93] mt-1">Each recipient gets the top opportunities and any high-conviction WES signals when this scan completes.</p>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#8A8A93]">
            <input data-testid="schedule-enabled" type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            Enabled
          </label>
        </div>
        <div className="flex gap-2 mt-6">
          <button type="button" onClick={onClose} className="flex-1 border border-[#3F3F47] text-white py-2.5 rounded-sm text-sm">Cancel</button>
          <button data-testid="schedule-submit" type="submit" disabled={submitting} className="flex-1 bg-[#00FF88] text-[#08080A] font-bold py-2.5 rounded-sm text-sm disabled:opacity-40">{submitting ? "Saving…" : (initial ? "Save Changes" : "Create Schedule")}</button>
        </div>
      </form>
    </div>
  );
}
